import type { ApiClient, ApiClientError } from '@acies/platform';
import { Effect, Schema } from 'effect';
import { HttpBody, HttpClientRequest, HttpClientResponse } from 'effect/http';
import { InventoryItemConfigurationReference, InventoryItemReference, type IStockConfigurationParameter, StockConfiguration, StockConfigurationParameters } from '../domain/configurations';
import { PositiveInteger } from '../domain/shared';
import { type ApiServices, runCall } from './runtime';
import { callDependencies, type StockItemPath } from './shared';

/**
 * Where configurations live, in full: the same resource a model's configuration is made at.
 *
 * Not an `EnginePath`, for the reason `services/configurations.ts` gives: it sits at the API's own root, and which store a configuration is for is said in the body.
 */
const resource = '/configurator/configurations';

/** Which kind of configuration is being asked for: a stock item's. A literal, because a body naming another kind is not this module's to send. */
const ConfigurationTypeSelf = Schema.Literal('/configurator/configuration-types/stock');

/**
 * The body a stock configuration is asked for with.
 *
 * The item and its configuration are typed by the domain's request schemas — see {@link InventoryItemReference} — so the prefixed links a search or an answer carries do not compile here.
 *
 * The parameters are left out of a first configuration, which is what asks for "whatever the item says" — where configuring one starts, as a model's empty list of parameters is. A configuration made again from the answers sends them, in the envelope they came in, as a model's does.
 *
 * The item configuration is left out until one has been picked: a new stock position is configured as its item alone, and the engine answers with the list its configurations are picked from — see `StockConfiguration.itemConfigurations`. A configuration asked for again with one picked names it, and sends no parameters,
 * since the answers it had were to the questions another configuration asked.
 *
 * The quantity goes with it, as it does for a model: how many of the item are wanted is the position's, and the calculation that prices the configuration reads it from there.
 */
const StockConfigurationBody = Schema.Struct({
	item: Schema.Struct({ self: InventoryItemReference }),
	itemConfiguration: Schema.optionalKey(Schema.Struct({ self: InventoryItemConfigurationReference })),
	parameters: Schema.optionalKey(Schema.Struct({ items: Schema.Array(StockConfigurationParameters) })),
	quantity: PositiveInteger,
	type: Schema.Struct({ self: ConfigurationTypeSelf }),
});

const encodeStockConfigurationBody = HttpClientRequest.schemaBodyJson(StockConfigurationBody);

/** The stock configuration itself, unwrapped: this resource answers at the top level, with nothing to unwrap. */
const decodeStockConfiguration = HttpClientResponse.schemaBodyJson(StockConfiguration);

/**
 * What a stock configuration is asked for with: the item, the configuration it comes in, and how many of it.
 *
 * Three fields rather than a whole `StockPosition`, because the configuration the item comes in is not the position's: it is picked after the position has been configured as its item, and named by the configuration — see `StockConfiguration.itemConfigurationKey`. Whoever asks says which.
 */
export interface StockConfigurationRequest {
	/** The configuration the item comes in, by its code — `K001` — or null while none has been picked. */
	readonly itemConfiguration: string | null;
	/** The item, by its item number — `PERSIENNE`. */
	readonly key: string;
	/** How many of the item are wanted. */
	readonly quantity: number;
}

/**
 * Everything making a stock configuration can fail with: the three a POST that encodes a body and decodes an answer can fail with, as a model's configuration can.
 *
 * An item the store does not have, or a configuration it does not come in, is the API refusing the request, and arrives as an {@link ApiClientError} like any other refusal.
 */
export type CreateStockConfigurationError = ApiClientError | HttpBody.HttpBodyError | Schema.SchemaError;

/** The calls against the Configurator's stock configurations. */
export interface StockConfigurationCalls {
	/** Makes a configuration of one inventory item, in the configuration it comes in, and answers with the item's defaults. */
	readonly createStockConfiguration: (request: StockConfigurationRequest) => Effect.Effect<StockConfiguration, CreateStockConfigurationError>;
	/**
	 * Makes a configuration of the same item with the answers it is given, and answers with it as the API made it.
	 *
	 * How a changed answer, or a changed count, is taken, the way a model's is — see `ConfigurationCalls.createConfigurationFrom`: nothing is changed in place, and a new configuration is asked for with the answers in the body. The answers are the ones to send — the caller leaves out those nobody has answered, see
	 * `answeredOf` — and go over as they came, `self` and all.
	 *
	 * The item and its configuration are named from the request rather than read off the configuration being answered: the engine answers with them prefixed, and the POST takes them plain.
	 */
	readonly createStockConfigurationFrom: (request: StockConfigurationRequest, items: ReadonlyArray<IStockConfigurationParameter>) => Effect.Effect<StockConfiguration, CreateStockConfigurationError>;
}

/** Implements the stock configuration calls over the client and the way an item is named to the API. */
export function makeStockConfigurationCalls(client: typeof ApiClient.Service, stockItemPath: StockItemPath): StockConfigurationCalls {
	/** Asks for a stock configuration of the request's item, with the answers when there are any to send. */
	const configure = ({ itemConfiguration, key, quantity }: StockConfigurationRequest, items: ReadonlyArray<IStockConfigurationParameter> | null) =>
		Effect.gen(function* () {
			const reference = yield* stockItemPath(key, itemConfiguration);
			const request = yield* encodeStockConfigurationBody(HttpClientRequest.post(resource), {
				item: { self: reference.item },
				...(reference.itemConfiguration === null ? {} : { itemConfiguration: { self: reference.itemConfiguration } }),
				...(items === null ? {} : { parameters: { items } }),
				quantity,
				type: { self: '/configurator/configuration-types/stock' },
			});
			const response = yield* client.execute(request);
			return yield* decodeStockConfiguration(response);
		});
	return {
		createStockConfiguration: (request) => configure(request, null),
		createStockConfigurationFrom: (request, items) => configure(request, items),
	};
}

/**
 * Makes a configuration of one inventory item, from outside Effect.
 *
 * The awaitable form of {@link StockConfigurationCalls.createStockConfiguration}. A failure comes back as itself rather than as a bare `false` — see {@link runCall} — because what stopped a position being configured is what has to be said about it.
 */
export const createStockConfiguration = (request: StockConfigurationRequest): Promise<StockConfiguration | CreateStockConfigurationError> => runCall(() => createStockConfigurationEffect(request));

/**
 * Makes a configuration of one inventory item, as an Effect over the Configurator's services.
 *
 * For a caller that forks the call rather than awaiting it, so it can be interrupted when it is overtaken — see `ConfiguratorMachine`. Interrupted, the request is aborted and nothing comes back.
 */
export const createStockConfigurationEffect = (request: StockConfigurationRequest): Effect.Effect<StockConfiguration, CreateStockConfigurationError, ApiServices> => callDependencies.pipe(Effect.flatMap(({ client, stockItemPath }) => makeStockConfigurationCalls(client, stockItemPath).createStockConfiguration(request)));

/**
 * Makes a configuration of the same item with the answers it is given, as an Effect over the Configurator's services.
 *
 * For a caller that forks the call rather than awaiting it, so a change overtaken by the next can be interrupted — see `ConfiguratorMachine`. Interrupted, the request is aborted and nothing comes back.
 */
export const createStockConfigurationFromEffect = (request: StockConfigurationRequest, items: ReadonlyArray<IStockConfigurationParameter>): Effect.Effect<StockConfiguration, CreateStockConfigurationError, ApiServices> =>
	callDependencies.pipe(Effect.flatMap(({ client, stockItemPath }) => makeStockConfigurationCalls(client, stockItemPath).createStockConfigurationFrom(request, items)));
